import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IsActiveMatchOptions, NavigationEnd, Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoModule } from '@jsverse/transloco';
import { filter } from 'rxjs/operators';
import { CollapsibleDirective } from '../../../shared/directives';

/** Prefix match — active when the URL is the path or a descendant. */
const SECTION_ACTIVE_MATCH: IsActiveMatchOptions = {
  paths: 'subset',
  queryParams: 'ignored',
  fragment: 'ignored',
  matrixParams: 'ignored',
};

/**
 * Collapsible sidenav section — uppercase label + chevron disclosure
 * wrapping a projected nav list.
 *
 * Mirrors the controls / import-assistant group-header pattern:
 * whole header is the toggle (not a 14px chevron hit-target),
 * `expand_more` rotates −90° when collapsed.
 *
 * Defaults open. Pass `activePaths` so navigating into a collapsed
 * section re-opens it once; the user can still collapse afterwards
 * without the effect fighting them.
 */
@Component({
  selector: 'app-sidebar-nav-group',
  imports: [MatIconModule, TranslocoModule, CollapsibleDirective],
  templateUrl: './sidebar-nav-group.html',
  styleUrl: './sidebar-nav-group.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarNavGroup {
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  /** Already-translated section label. */
  readonly label = input.required<string>();

  /**
   * Router paths that belong to this section. When any is active
   * (prefix match), the group expands. Empty = no auto-expand.
   */
  readonly activePaths = input<readonly string[]>([]);

  protected readonly open = signal(true);

  /** Bumped on NavigationEnd so `sectionActive` re-evaluates. */
  private readonly routeEpoch = signal(0);

  protected readonly sectionActive = computed(() => {
    this.routeEpoch();
    const paths = this.activePaths();
    return paths.some(path => this.router.isActive(path, SECTION_ACTIVE_MATCH));
  });

  constructor() {
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.routeEpoch.update(n => n + 1));

    effect(() => {
      if (this.sectionActive()) this.open.set(true);
    });
  }

  protected toggle(): void {
    this.open.update(v => !v);
  }
}
